import { DOCUMENT } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions, ScriptableContext, TooltipItem } from 'chart.js';
import { Uc2SignalTimelineRow } from '../../models/uc2-view-state';

const WAT_TIME_ZONE = 'Africa/Lagos';

@Component({
  selector: 'app-uc2-onset-timeline',
  imports: [BaseChartDirective],
  templateUrl: './uc2-onset-timeline.component.html',
  styleUrl: './uc2-onset-timeline.component.scss',
})
export class Uc2OnsetTimelineComponent {
  rows = input.required<Uc2SignalTimelineRow[]>();
  selectedMemberId = input<string | null>(null);

  memberSelected = output<string>();

  private _document = inject(DOCUMENT);

  chartData = computed<ChartData<'bar', ([number, number] | null)[], string>>(() => {
    this.selectedMemberId();
    const rows = this.rows();
    const intervalCount = Math.max(1, ...rows.map(row => row.intervals?.length ?? 1));

    return {
      labels: rows.map(row => row.asset),
      datasets: Array.from({ length: intervalCount }, (_, intervalIndex) => ({
        label: 'Pressure deviation',
        data: rows.map(row => row.intervals
          ? row.intervals[intervalIndex] ?? null
          : intervalIndex === 0 ? [row.startMs, row.endMs] : null),
        backgroundColor: () => this.themeColor('--status-amber'),
        borderColor: context => this.resolveSignalBorderColor(context),
        borderWidth: context => this.resolveSignalBorderWidth(context),
        borderSkipped: false,
        grouped: false,
      })),
    };
  });

  chartOptions = computed<ChartOptions<'bar'>>(() => {
    const rows = this.rows();

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      indexAxis: 'y',
      scales: {
        x: {
          type: 'linear',
          min: Math.min(...rows.map(row => row.startMs)),
          max: Math.max(...rows.map(row => row.endMs)),
          title: { display: true, text: 'Observation time (WAT)' },
          ticks: {
            autoSkip: true,
            autoSkipPadding: 16,
            maxRotation: 0,
            maxTicksLimit: 5,
            callback: value => this.formatAxisTime(Number(value)),
          },
        },
        y: {
          grid: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: items => this.tooltipTitle(items[0]),
            label: item => this.tooltipLines(item),
          },
        },
      },
    };
  });

  onChartClick(active: object[] | undefined): void {
    const first = active?.[0];
    const index = first && 'index' in first && typeof first.index === 'number' ? first.index : -1;
    const datasetIndex = first && 'datasetIndex' in first && typeof first.datasetIndex === 'number'
      ? first.datasetIndex
      : -1;
    const row = this.rows()[index];
    const interval = this.chartData().datasets[datasetIndex]?.data[index];
    if (row && interval) {
      this.memberSelected.emit(row.exceptionId);
    }
  }

  private resolveSignalBorderColor(context: ScriptableContext<'bar'>): string {
    const row = this.rows()[context.dataIndex];
    return row?.exceptionId === this.selectedMemberId()
      ? this.themeColor('--chv-dm-dark-gray')
      : this.themeColor('--status-amber');
  }

  private resolveSignalBorderWidth(context: ScriptableContext<'bar'>): number {
    const row = this.rows()[context.dataIndex];
    return row?.exceptionId === this.selectedMemberId() ? 4 : 1;
  }

  private tooltipTitle(item: TooltipItem<'bar'> | undefined): string {
    const row = this.rows()[item?.dataIndex ?? -1];
    return row?.signalClass ?? '';
  }

  private tooltipLines(item: TooltipItem<'bar'>): string[] {
    const row = this.rows()[item.dataIndex];
    const interval = this.chartData().datasets[item.datasetIndex]?.data[item.dataIndex];
    if (!row || !interval) {
      return [];
    }

    return [
      `Asset: ${row.asset}`,
      `Measurement: ${row.signalClass}`,
      `Start: ${this.formatTime(interval[0])} WAT`,
      ...row.context.map(entry => `${entry.label}: ${entry.value}`),
    ];
  }

  private formatTime(value: number): string {
    return new Date(value).toLocaleString(undefined, {
      timeZone: WAT_TIME_ZONE,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatAxisTime(value: number): string[] {
    const date = new Date(value);
    return [
      date.toLocaleDateString(undefined, {
        timeZone: WAT_TIME_ZONE,
        month: 'short',
        day: 'numeric',
      }),
      date.toLocaleTimeString(undefined, {
        timeZone: WAT_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
      }),
    ];
  }

  private themeColor(variable: string): string {
    return getComputedStyle(this._document.documentElement).getPropertyValue(variable).trim();
  }
}